/* eslint-disable @next/next/no-img-element */
"use client";

import DefaultPageLayout from "@/layouts/DefaultPageLayout";
import { Fragment, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalData } from "@/contexts/GlobalData";
import RequireLogin from "@/components/RequireLogin";
import DynamicSizeIcon from "@/components/DynamicSizeIcon";
import DynamicFontSize from "@/components/DynamicFontSize";
import OtpInputComponent from "@/components/OtpInput";
import UnderlineText from "@/components/UnderlineText";
import DetailPageLayout from "@/layouts/DetailPageLayout";
import { InputOtp } from "@nextui-org/input-otp";
import { LoginAppData, useAuth } from "@/contexts/Auth";
import getLoginPasswordInfo from "@/common/function/getLoginPasswordInfo";
import Link from "next/link";
import PasswordInput from "../PasswordInput";
import formatDate from "@/common/function/formatDate";
import { Keyboard, KeyboardType, THEME } from "tek-input-keyboard";
const prefix = "password_login_";
export default function LoginApp() {
  const [failedLoginTimes, setFailedLoginTimes] = useState(0);
  const [password, setPassword] = useState("");
  const {
    translate,
    getIcon,
    generalSetting
  } = useGlobalData();
  const {
    setLoginAppStep,
    loginAppData: loginData,
    loginAppStep
  } = useAuth();
  const [messageError, setMessageError] = useState("");
  const limitLoginTimes = generalSetting?.incorrect_passcode_times ?? 5;
  const lockedLoginTime = generalSetting?.locked_login_time ?? 86400;
  const handleOtpChange = (value: string) => {
    if (value?.length > 6) return;
    setPassword(value);
    if (value?.length === 6) {
      if (loginData?.password === value) {
        setLoginAppStep("logged");
        setMessageError("");
        localStorage.removeItem("failedLoginTimes");
        localStorage.removeItem("loginLockedUntil");
      } else {
        localStorage.setItem("failedLoginTimes", failedLoginTimes + 1 + "");
        setFailedLoginTimes(prev => prev + 1);
        setLoginAppStep("login");
        if (failedLoginTimes + 1 >= limitLoginTimes) {
          const untilTime = new Date(new Date().getTime() + lockedLoginTime * 1000)?.toISOString();
          localStorage.setItem("loginLockedUntil", untilTime + "");
          setMessageError(`${translate(prefix + "by login incorrect passcode over")} ${limitLoginTimes} ${translate(prefix + "times")} ${translate(prefix + "you be locked until")} ${formatDate(untilTime)}`);
          return;
        }
        setMessageError(`${translate(prefix + "invalid password your will be locked")} ${translate(prefix + (lockedLoginTime ?? "blocked time"))} ${translate(prefix + "if enter incorrect passcode over")} ${limitLoginTimes} ${translate(prefix + "times")}
                    `);
        setPassword("");
      }
    } else {
      if (!!password) {
        setMessageError("");
      }
      setLoginAppStep("login");
    }
  };
  useEffect(() => {
    try {
      const failedLoginTimes = +(localStorage.getItem("failedLoginTimes") ?? 0);
      setFailedLoginTimes(failedLoginTimes);
      if (failedLoginTimes >= limitLoginTimes) {
        const loginLockedUntil = localStorage.getItem("loginLockedUntil");
        if (loginLockedUntil) {
          const untilTime = new Date(loginLockedUntil);
          const currentTime = new Date().getTime();
          if (currentTime >= untilTime.getTime()) {
            localStorage.removeItem("failedLoginTimes");
            localStorage.removeItem("loginLockedUntil");
            setFailedLoginTimes(0);
            return;
          }
          setFailedLoginTimes(failedLoginTimes);
          setMessageError(`${translate(prefix + "by login incorrect passcode over")} ${limitLoginTimes} ${translate(prefix + "times")} ${translate(prefix + "you be locked until")} ${formatDate(untilTime.toISOString())}`);
        }
      }
    } catch (err) {
      console.log(err);
    }
  }, [limitLoginTimes]);
  return <DetailPageLayout disableScroll disabledAnimation data-sentry-element="DetailPageLayout" data-sentry-component="LoginApp" data-sentry-source-file="page.tsx">
            <Keyboard keyboardType={KeyboardType.Text} theme={THEME.DARK} alwaysOpen onChange={value => {
      handleOtpChange(value);
    }} value={password} toolbarFullHeight trigger={<div>Click</div>} classNames={{
      keyboardContainer: loginAppStep == "login" ? "z-[9999999]" : "z-[999]"
    }} toolbar={<div className="w-full flex flex-col py-6 px-4 items-center h-full justify-center">
                        <div className="flex flex-col gap-1 items-center">
                            <DynamicSizeIcon icon={getIcon("logo")} width={48}></DynamicSizeIcon>
                            <DynamicFontSize fontSize={17} className="text-white font-medium">
                                {translate(prefix + "title")}
                            </DynamicFontSize>
                            <DynamicFontSize fontSize={14} className="text-description">
                                {translate(prefix + "des")}
                            </DynamicFontSize>
                        </div>
                        <div className="flex flex-col gap-3 items-center text-white">
                            {failedLoginTimes < limitLoginTimes && <>
                                    <DynamicFontSize fontSize={12}>
                                        {translate(prefix + "enter your password")}
                                    </DynamicFontSize>
                                    <div className="!w-[350px] px-4">
                                        <PasswordInput type="password" value={password} readOnly />
                                    </div>
                                    {messageError && <DynamicFontSize fontSize={12} className="text-error italic">
                                            {messageError}
                                        </DynamicFontSize>}
                                </>}

                            {failedLoginTimes >= limitLoginTimes && <DynamicFontSize fontSize={12} className="text-error italic self-start">
                                    {messageError}
                                </DynamicFontSize>}

                            <DynamicFontSize fontSize={12} onClick={() => setLoginAppStep("forgot")} className="self-end">
                                <UnderlineText>
                                    {failedLoginTimes >= limitLoginTimes ? translate(prefix + "reset password") : translate(prefix + "forgot password")}
                                </UnderlineText>
                            </DynamicFontSize>
                        </div>
                    </div>} data-sentry-element="Keyboard" data-sentry-source-file="page.tsx" />
        </DetailPageLayout>;
}